import { useEffect, useRef } from 'react'
import { useUIStore } from '@/stores/uiStore'

const POLL_INTERVAL_MS = 5 * 60 * 1000
const VERSION_URL = '/version.json'

interface VersionPayload {
  version?: string
  builtAt?: string
}

export function useVersionCheck(): void {
  const setUpdateAvailable = useUIStore((s) => s.setUpdateAvailable)
  const currentVersion = import.meta.env.VITE_APP_VERSION as string | undefined
  const notifiedRef = useRef(false)

  useEffect(() => {
    if (!currentVersion || currentVersion === 'unknown') return

    let cancelled = false

    const check = async () => {
      if (notifiedRef.current) return
      try {
        const res = await fetch(`${VERSION_URL}?t=${String(Date.now())}`, {
          cache: 'no-store',
          credentials: 'same-origin',
        })
        if (!res.ok) return
        const data = (await res.json()) as VersionPayload
        if (cancelled) return
        if (data.version && data.version !== currentVersion) {
          notifiedRef.current = true
          setUpdateAvailable(true)
        }
      } catch {
        // Network hiccup — try again on next tick.
      }
    }

    void check()

    const intervalId = window.setInterval(() => {
      void check()
    }, POLL_INTERVAL_MS)

    const onFocus = () => {
      void check()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check()
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [currentVersion, setUpdateAvailable])
}
